import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { resolvePdfPreviewPixelRatio } from './pdf-preview-render.helpers';
import {
  reportUnhandledPdfRenderError,
  runWithPdfPage,
} from './pdf-page-cleanup.helpers';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export {
  PDF_PREVIEW_MAX_RENDER_EDGE_PX,
  resolvePdfPreviewPixelRatio,
} from './pdf-preview-render.helpers';

/** Cap the long edge so Mapbox textures stay bounded. */
const MAX_RASTER_EDGE_PX = 2048;
const JPEG_QUALITY = 0.72;
/** Large enough for a readable page-picker rail (~160–180 CSS px). */
const THUMBNAIL_MAX_EDGE_PX = 180;
/** PDF user units are 1/72 inch. */
const PDF_POINTS_PER_INCH = 72;
const MM_PER_INCH = 25.4;

export type RasterizedPdfPage = {
  imageDataUrl: string;
  pixelWidth: number;
  pixelHeight: number;
  pageCount: number;
  pageIndex: number;
  /** PDF media-box width in millimeters. */
  paperWidthMm: number;
  /** PDF media-box height in millimeters. */
  paperHeightMm: number;
};

export type PdfPageThumbnail = {
  pageIndex: number;
  imageDataUrl: string;
  /** Optional PDF page label / name when present. */
  pageName?: string;
};

export type LoadedPdfDocument = {
  doc: PDFDocumentProxy;
  pageCount: number;
  /** Per-page label from the PDF (same length as pageCount). */
  pageNames: readonly (string | undefined)[];
  destroy: () => Promise<void>;
};

/**
 * Open a PDF once for the import wizard (thumbs + live preview share this).
 */
export const loadPdfDocument = async (
  file: File,
): Promise<LoadedPdfDocument> => {
  const data = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data });
  const doc = await loadingTask.promise;
  const pageCount = doc.numPages;
  const labels = await doc.getPageLabels();
  const pageNames = Array.from({ length: pageCount }, (_, index) => {
    const label = labels?.[index]?.trim();
    return label || undefined;
  });
  return {
    doc,
    pageCount,
    pageNames,
    destroy: async () => {
      await doc.cleanup();
      await loadingTask.destroy();
    },
  };
};

/**
 * Rasterize every page to a small JPEG thumbnail for the page picker rail.
 */
export const rasterizePdfThumbnails = async (
  doc: PDFDocumentProxy,
  pageNames: readonly (string | undefined)[] = [],
): Promise<{ pageCount: number; thumbnails: PdfPageThumbnail[] }> => {
  const pageCount = doc.numPages;
  const thumbnails: PdfPageThumbnail[] = [];
  for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
    const page = await doc.getPage(pageIndex + 1);
    const imageDataUrl = await runWithPdfPage(page, (pdfPage) =>
      renderPageToDataUrl(pdfPage, {
        maxEdgePx: THUMBNAIL_MAX_EDGE_PX,
        quality: 0.65,
      }),
    );
    thumbnails.push({
      pageIndex,
      imageDataUrl,
      pageName: pageNames[pageIndex],
    });
  }
  return { pageCount, thumbnails };
};

type BeginPdfPageCanvasRenderOptions = {
  cssWidth: number;
  cssHeight: number;
  devicePixelRatio?: number;
  /**
   * Extra backing-store density for CSS zoom (viewport.scale). CSS size stays
   * fit-to-pane; pixels scale up so measure zoom does not look soft.
   */
  renderBoost?: number;
  /** Long-edge cap for the canvas bitmap (see {@link resolvePdfPreviewPixelRatio}). */
  maxEdgePx?: number;
};

/**
 * Start painting one PDF page into an existing canvas, fitted inside the CSS box.
 * Returns the in-flight {@link pdfjs.RenderTask} (caller awaits / cancels).
 * Used for step 1/3 live viewing — does not bake a JPEG into project state.
 * When {@link BeginPdfPageCanvasRenderOptions.renderBoost} is set (calibrate
 * zoom), the bitmap is denser than CSS so CSS `scale()` stays readable.
 */
export const beginPdfPageCanvasRender = async (
  doc: PDFDocumentProxy,
  pageIndex: number,
  canvas: HTMLCanvasElement,
  options: BeginPdfPageCanvasRenderOptions,
): Promise<pdfjs.RenderTask> => {
  if (pageIndex < 0 || pageIndex >= doc.numPages) {
    throw new Error(
      `PDF page ${pageIndex + 1} is out of range (1–${doc.numPages})`,
    );
  }
  const page = await doc.getPage(pageIndex + 1);
  try {
    const task = await startPdfPageCanvasRender(page, canvas, options);
    // Preview owns this proxy until the paint settles (including cancel).
    // `.finally()` re-rejects; catch cancelled so `void` is not an unhandled rejection.
    void task.promise
      .finally(() => page.cleanup())
      .catch(reportUnhandledPdfRenderError);
    return task;
  } catch (error) {
    page.cleanup();
    throw error;
  }
};

/**
 * Paint {@link page} into {@link canvas} without releasing the page proxy.
 */
const startPdfPageCanvasRender = async (
  page: PDFPageProxy,
  canvas: HTMLCanvasElement,
  options: BeginPdfPageCanvasRenderOptions,
): Promise<pdfjs.RenderTask> => {
  const dpr = options.devicePixelRatio ?? window.devicePixelRatio ?? 1;
  const cssWidth = Math.max(1, options.cssWidth);
  const cssHeight = Math.max(1, options.cssHeight);
  // Fit in CSS pixels first, then apply pixel ratio via canvas transform.
  const baseViewport = page.getViewport({ scale: 1 });
  const fitScale = Math.min(
    cssWidth / baseViewport.width,
    cssHeight / baseViewport.height,
  );
  const viewport = page.getViewport({ scale: fitScale });
  const outputWidth = Math.max(1, Math.floor(viewport.width));
  const outputHeight = Math.max(1, Math.floor(viewport.height));
  const pixelRatio = resolvePdfPreviewPixelRatio({
    cssWidth: outputWidth,
    cssHeight: outputHeight,
    devicePixelRatio: dpr,
    renderBoost: options.renderBoost,
    maxEdgePx: options.maxEdgePx,
  });

  canvas.width = Math.max(1, Math.floor(outputWidth * pixelRatio));
  canvas.height = Math.max(1, Math.floor(outputHeight * pixelRatio));
  canvas.style.width = `${outputWidth}px`;
  canvas.style.height = `${outputHeight}px`;

  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create canvas for PDF preview');
  // Reset any prior transform before applying HiDPI / zoom boost scale.
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.clearRect(0, 0, canvas.width, canvas.height);

  const transform =
    pixelRatio !== 1
      ? ([pixelRatio, 0, 0, pixelRatio, 0, 0] as const)
      : undefined;
  // pdf.js: pass `canvas` only — `canvasContext` together with `canvas` can cancel the paint.
  return page.render({
    canvas,
    viewport,
    ...(transform ? { transform: [...transform] } : {}),
  });
};

/**
 * Read PDF media-box size in millimeters for a page (no rasterize).
 */
export const readPdfPagePaperSizeMm = async (
  doc: PDFDocumentProxy,
  pageIndex: number,
): Promise<{ paperWidthMm: number; paperHeightMm: number }> => {
  const pageCount = doc.numPages;
  if (pageIndex < 0 || pageIndex >= pageCount) {
    throw new Error(
      `PDF page ${pageIndex + 1} is out of range (1–${pageCount})`,
    );
  }
  const page = await doc.getPage(pageIndex + 1);
  return runWithPdfPage(page, async (pdfPage) => {
    const baseViewport = pdfPage.getViewport({ scale: 1 });
    return {
      paperWidthMm: (baseViewport.width / PDF_POINTS_PER_INCH) * MM_PER_INCH,
      paperHeightMm: (baseViewport.height / PDF_POINTS_PER_INCH) * MM_PER_INCH,
    };
  });
};

/**
 * Rasterize from an already-open document (place step — same quality as file API).
 */
export const rasterizePdfPageFromDoc = async (
  doc: PDFDocumentProxy,
  pageIndex: number,
): Promise<Omit<RasterizedPdfPage, 'pageCount'> & { pageCount: number }> => {
  const pageCount = doc.numPages;
  if (pageIndex < 0 || pageIndex >= pageCount) {
    throw new Error(
      `PDF page ${pageIndex + 1} is out of range (1–${pageCount})`,
    );
  }
  const page = await doc.getPage(pageIndex + 1);
  return runWithPdfPage(page, async (pdfPage) => {
    const baseViewport = pdfPage.getViewport({ scale: 1 });
    const paperWidthMm =
      (baseViewport.width / PDF_POINTS_PER_INCH) * MM_PER_INCH;
    const paperHeightMm =
      (baseViewport.height / PDF_POINTS_PER_INCH) * MM_PER_INCH;
    const { imageDataUrl, pixelWidth, pixelHeight } = await renderPageToCanvas(
      pdfPage,
      { maxEdgePx: MAX_RASTER_EDGE_PX, quality: JPEG_QUALITY },
    );
    return {
      imageDataUrl,
      pixelWidth,
      pixelHeight,
      pageCount,
      pageIndex,
      paperWidthMm,
      paperHeightMm,
    };
  });
};

type RenderOptions = {
  maxEdgePx: number;
  quality: number;
};

const renderPageToDataUrl = async (
  page: PDFPageProxy,
  options: RenderOptions,
): Promise<string> => {
  const { imageDataUrl } = await renderPageToCanvas(page, options);
  return imageDataUrl;
};

const renderPageToCanvas = async (
  page: PDFPageProxy,
  options: RenderOptions,
): Promise<{
  imageDataUrl: string;
  pixelWidth: number;
  pixelHeight: number;
}> => {
  const baseViewport = page.getViewport({ scale: 1 });
  const longEdge = Math.max(baseViewport.width, baseViewport.height);
  // Honor maxEdgePx fully so place rasters stay sharp on typical pages.
  const scale = options.maxEdgePx / longEdge;
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.floor(viewport.width));
  canvas.height = Math.max(1, Math.floor(viewport.height));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not create canvas for PDF rasterize');

  const renderTask = page.render({ canvas, viewport });
  await renderTask.promise;

  return {
    imageDataUrl: canvas.toDataURL('image/jpeg', options.quality),
    pixelWidth: canvas.width,
    pixelHeight: canvas.height,
  };
};
